import React from 'react';
import PropTypes from 'prop-types';
import Layout, { ContentSection } from '~components/Layout';
import { graphql } from 'gatsby';
import Helmet from 'react-helmet';
import ExternalLinkIcon from '~components/common/ExternalLinkIcon';
import StatePageAccordion, {
  AccordionGroup,
} from '~components/common/Accordion/StatePageAccordion';
import StateDeadlinesSection from '~components/common/StateDeadlinesSection';
import { formatDateFriendly } from '~utils/formatDate';
import Markdown from '~components/common/Markdown';
import {
  doINeedToUpdateRegistrationAfterMove,
  amIRegistered,
  canIRegisterOnElectionDay,
} from '~utils/faqAnswersRegister';
import {
  canIHaveABallotMailedToMe,
  howDoIReturnMyBallot,
  howDoIVoteIfMilitaryOrOverseas,
} from '~utils/faqAnswersAbsentee';
import {
  canIVoteEarly,
  canIUseStudentId,
  whatDoINeedToBringToThePolls,
  whereDoIVote,
} from '~utils/faqAnswersInPersonVoting';
import { whatIfIHaveOtherQuestions } from '~utils/faqAnswersContactsAndReferences';
import { registrationNotRequired } from '~components/Register/specialCases';
import Badge from '~components/common/Badge';

function ElectionPage({ data }) {
  const election = data.voteAmericaElection;
  const state = data.voteAmericaState;

  const electionDate = new Date(election.date);
  const electionDateFriendly = formatDateFriendly(electionDate);
  const electionYear = electionDate.getFullYear();

  const electionTitle = `${election.district_name} ${election.type} election`;

  const electionDescription = election.description ?? electionTitle;

  const title = `${electionDescription} - ${electionYear} - VoteAmerica`;
  const description = `Get all the details on the ${electionYear} ${electionDescription} and deadlines for voting in ${state.name}`;

  // DC is not included in the same url pattern as the states. This is a general page for DC.
  const dcURL = `https://ballotpedia.org/Washington,_D.C.`;
  const stateDC = state.code === 'DC';

  // reduce state_information array to hash, so we can access more easily in template
  const stateInfo = state.state_information.reduce((map, obj) => {
    if (obj && obj.field) {
      // eslint-disable-next-line no-param-reassign
      map[obj.field.slug] = {
        text: obj.text,
        html: obj.html,
        bool_value: obj.bool_value,
        multiselect_value: obj.multiselect_value,
        footnotes: obj.footnotes,
      };
    }
    return map;
  }, {});

  return (
    <Layout title={title} description={description}>
      <Helmet>
        <script type='application/ld+json'>
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Election",
                "item": "https://www.voteamerica.org/election/"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "Election - ${electionDescription}"
              }]
            }
          `}
        </script>
      </Helmet>
      <ContentSection width='mid'>
        <h1>{electionTitle}</h1>
        <Badge>{electionDateFriendly}</Badge>
        <ul>
          <li>
            <span className='font-bold'>Election type:</span>{' '}
            <span className='capitalize'>{election.type}</span>
          </li>
          <li>
            <span className='font-bold'>Region:</span> {election.district_name}
            {!election.district_name.includes(state.name) && (
              <span>, {state.name}</span>
            )}
          </li>
          {election.description && (
            <li>
              <span className='font-bold'>Description:</span>{' '}
              {election.description}
            </li>
          )}
        </ul>
        <p className='mb-0'>
          Visit{' '}
          <a
            href={
              stateDC
                ? dcURL
                : `https://ballotpedia.org/${state.name}_elections,_${electionYear}`
            }
            target='_blank'
            title={`${state.name} elections at Ballotpedia.org`}
            rel='noopener'
          >
            Ballotpedia.org
          </a>
          <ExternalLinkIcon /> to learn what you&apos;re voting on this
          election.
        </p>
      </ContentSection>
      <StateDeadlinesSection
        state={state}
        stateInfo={stateInfo}
        electionDate={electionDate}
      />
      <ContentSection width='mid'>
        <h2>Questions about voting in {state.name}</h2>

        <AccordionGroup>
          <StatePageAccordion heading='When is Election Day?'>
            <p>
              Election Day is {electionDateFriendly}.{' '}
              {stateInfo.gov_tool_polling_place.text && (
                <>
                  <a
                    href={stateInfo.gov_tool_polling_place.text}
                    title={`${state.name} polling place lookup site`}
                  >
                    Find your polling place here
                  </a>
                  .
                </>
              )}
            </p>
          </StatePageAccordion>

          <StatePageAccordion heading='Can I vote early?'>
            <Markdown
              content={canIVoteEarly(stateInfo, state.name, state.code)}
            />
          </StatePageAccordion>

          <StatePageAccordion heading='Where do I vote?'>
            <Markdown
              content={whereDoIVote(stateInfo, state.name, state.code)}
            />
          </StatePageAccordion>

          <StatePageAccordion heading='What type of ID do I need to show when I vote?'>
            <Markdown content={whatDoINeedToBringToThePolls(stateInfo)} />
          </StatePageAccordion>

          <StatePageAccordion heading='Can I use my student ID as identification when voting?'>
            <Markdown content={canIUseStudentId(stateInfo)} />
          </StatePageAccordion>

          <StatePageAccordion heading='Can I have a ballot mailed to me?'>
            <Markdown
              content={canIHaveABallotMailedToMe(
                stateInfo,
                state.name,
                state.code
              )}
            />
          </StatePageAccordion>

          <StatePageAccordion heading='I have a ballot. How do I return it?'>
            <Markdown
              content={howDoIReturnMyBallot(stateInfo, state.name, state.code)}
            />
          </StatePageAccordion>

          <StatePageAccordion heading="How do I know if I'm registered to vote?">
            <Markdown
              content={amIRegistered(stateInfo, state.name, state.code)}
            />
          </StatePageAccordion>

          {!registrationNotRequired.includes(state.code) && (
            <StatePageAccordion heading='Can I register to vote on Election Day?'>
              <Markdown
                content={canIRegisterOnElectionDay(
                  stateInfo,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
          )}

          {!registrationNotRequired.includes(state.code) && (
            <StatePageAccordion heading="I've moved. Do I have to update my registration?">
              <Markdown
                content={doINeedToUpdateRegistrationAfterMove(
                  stateInfo,
                  state.name,
                  state.code
                )}
              />
            </StatePageAccordion>
          )}

          <StatePageAccordion heading="How do I vote if I'm in the military or overseas?">
            <Markdown content={howDoIVoteIfMilitaryOrOverseas(stateInfo)} />
          </StatePageAccordion>

          <StatePageAccordion heading='What if I have other questions?'>
            <Markdown content={whatIfIHaveOtherQuestions(state.code)} />
          </StatePageAccordion>
        </AccordionGroup>
      </ContentSection>
    </Layout>
  );
}

ElectionPage.propTypes = {
  data: PropTypes.shape({
    voteAmericaElection: PropTypes.shape({
      date: PropTypes.string,
      type: PropTypes.string,
      district_type: PropTypes.string,
      district_name: PropTypes.string,
      description: PropTypes.string,
    }),
    voteAmericaState: PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
      state_information: PropTypes.array,
    }),
  }),
};

export default ElectionPage;

export const pageQuery = graphql`
  query ElectionById($id: String, $state_code: String) {
    voteAmericaElection(election_external_id: { eq: $id }) {
      date
      type
      district_type
      district_name
      description
    }
    voteAmericaState(code: { eq: $state_code }) {
      name
      code
      state_information {
        html
        text
        bool_value
        multiselect_value
        footnotes
        field {
          slug
        }
      }
    }
  }
`;
